import EditorJs from '@editorjs/editorjs';
import copyToClipboard from 'copy-to-clipboard';

import { KeyboardListenerParams, Block } from '../types';
import {
  InstructionAttribute,
  InstructionCSSClasses,
  InstructionEvent,
} from '../../../../components/script-editor/tools/types';
import { emitInstructionEventByEditor } from '../../../../components/script-editor/tools/utils';
import { EditorData } from '../../types';
import { InstructionType, InstructionTypes } from '../../../../types/instructions';

/* eslint-disable @typescript-eslint/no-explicit-any, lines-between-class-members, no-param-reassign, no-await-in-loop, no-plusplus */

class CopyHandler {
  private textFormat = 'text/plain';
  private editorFormat = 'application/x-editor-js';
  protected editor: EditorJs;
  protected data: EditorData;
  protected copyInstructions: any[] = [];
  protected copyBlocks: Block[] = [];
  protected textData: string = '';

  constructor({ editor, data }: KeyboardListenerParams) {
    this.data = data;
    this.editor = editor;
  }

  async process() {
    await this.processSelectedBlocks();
    if (this.copyBlocks.length === 0) {
      this.processSelectedFragment();
    }
    if (this.copyBlocks.length === 0) {
      this.copyAllData();
    }
    if (this.copyToClipboard()) {
      this.notifySuccess();
    } else {
      this.notifyFail();
    }
  }

  protected copyToClipboard(): boolean {
    if (this.copyBlocks.length > 0) {
      return copyToClipboard(
        JSON.stringify({
          blocks: this.copyBlocks,
          instructions: this.copyInstructions,
        }),
        // @ts-ignore
        {
          format: this.editorFormat,
          onCopy: this.onCopyAdditionalFormat.bind(this),
        },
      );
    }

    return false;
  }

  protected copyAllData() {
    this.data.blocks.forEach((block) => {
      this.processBlock(block as Block);
    });
  }

  protected onCopyAdditionalFormat(clipboardData: DataTransfer | null) {
    try {
      if (clipboardData) {
        clipboardData.setData(
          this.textFormat,
          this.textData,
        );
      }
    } catch (err) {
      console.error(err);
    }
  }

  protected notifySuccess() {
    emitInstructionEventByEditor(
      this.editor,
      InstructionEvent.NOTIFY,
      {
        type: 'success',
        message: 'Script text & instructions copied.',
        icon: 'copy',
      },
    );
  }

  protected notifyFail() {
    emitInstructionEventByEditor(
      this.editor,
      InstructionEvent.NOTIFY,
      {
        type: 'error',
        message: 'Copying failed.',
        icon: 'copy',
      },
    );
  }

  async processSelectedBlocks(): Promise<void> {
    const blocksCount = this.editor.blocks.getBlocksCount();
    for (let i = 0; i < blocksCount; i++) {
      const block = this.editor.blocks.getBlockByIndex(i);
      if (block && block.selected) {
        const blockData = await block.save();
        if (blockData) {
          this.processBlock({
            type: blockData.tool,
            data: blockData.data,
          });
        }
      }
    }
  }

  protected processSelectedFragment() {
    const selection = window.getSelection();
    if (selection && selection.rangeCount >= 1) {
      const content = selection.getRangeAt(0).cloneContents();
      const html = document.createElement('div');
      html.append(...content.childNodes);

      if (this.isExistInlineInstructionInInnerHtml(html.innerHTML)) {
        this.processInlineInstructionsByFragment(html);
        return;
      }
      this.transformHtmlBlockToText(html);
      if (html.innerHTML.trim()) {
        const block = {
          type: 'paragraph',
          data: {
            text: html.innerHTML,
          },
        };

        this.copyBlocks.push(block as Block);
      }
    }
  }

  processBlock(block: Block) {
    const blockInstructionId = block?.data?.instructionId;
    if (blockInstructionId) {
      this.processBlockInstruction(block);
      return;
    }
    if (this.isExistInlineInstructionInInnerHtml(block.data?.text || '')) {
      this.processInlineInstructionsByBlock(block);
      return;
    }
    this.transformHtmlBlockToText(this.transformBlockTextToHtmlElement(block.data.text));
    this.copyBlocks.push(block);
  }

  protected getInstructionById(id: string): InstructionType|undefined {
    return this.data.instructions.find(
      (item: InstructionType) => item?.id === id,
    );
  }

  protected transformBlockTextToHtmlElement(text: string): HTMLElement {
    const html = document.createElement('div');
    html.innerHTML = text;
    return html;
  }

  protected transformHtmlBlockToText(element: HTMLElement) {
    this.textData += `${element.textContent}\n`;
  }

  protected isExistInlineInstructionInInnerHtml(text: string): boolean {
    return text.indexOf(InstructionCSSClasses.INLINE_INSTRUCTION_WRAPPER) !== -1;
  }

  protected processBlockInstruction(block: Block) {
    const blockInstruction = this.getInstructionById(block.data.instructionId as string);
    if (blockInstruction) {
      this.copyInstructions.push(blockInstruction);
      this.copyBlocks.push(block);

      this.convertBlockInstructionToText(block, blockInstruction);
    }
  }

  protected convertBlockInstructionToText(block: Block, instruction: InstructionType): void {
    const instructionName = this.instructionName[instruction.type];
    const htmlBlock = document.createElement('div');
    htmlBlock.innerHTML = block.data.text;
    this.textData += `[${instructionName}: ${htmlBlock.textContent}]\n`;
  }

  protected processInlineInstructionsByBlock(block: Block): void {
    const htmlBlock = document.createElement('div');
    htmlBlock.innerHTML = block.data.text;
    const inlineInstructions = htmlBlock.querySelectorAll(`.${InstructionCSSClasses.INLINE_INSTRUCTION_WRAPPER}`);

    inlineInstructions.forEach((element) => {
      const instructionId = element.getAttribute(InstructionAttribute.instructionId);
      if (instructionId) {
        const currentInstruction = this.data.instructions.find(
          (item: InstructionType) => item?.id === instructionId,
        ) as InstructionType;
        if (currentInstruction) {
          this.copyInstructions.push(currentInstruction);
          const instructionName = this.instructionName[currentInstruction.type];
          element.innerHTML = `[${instructionName}: ${element.textContent}]`;
        }
      }
    });
    this.copyBlocks.push(block);
    this.textData += `${htmlBlock.textContent}\n`;
  }

  protected processInlineInstructionsByFragment(html:HTMLElement): void {
    const htmlElement = document.createElement('div');
    htmlElement.innerHTML = html.innerHTML;
    const inlineInstructions = htmlElement.querySelectorAll(`.${InstructionCSSClasses.INLINE_INSTRUCTION_WRAPPER}`);
    inlineInstructions.forEach((element) => {
      const instructionId = element.getAttribute(InstructionAttribute.instructionId);
      if (instructionId) {
        const currentInstruction = this.data.instructions.find(
          (item: InstructionType) => item?.id === instructionId,
        ) as InstructionType;
        if (currentInstruction) {
          this.copyInstructions.push(currentInstruction);
          const instructionName = this.instructionName[currentInstruction.type];
          const instructionHtml = htmlElement.querySelector(`[instruction-id="${instructionId}"]`);
          if (instructionHtml) {
            instructionHtml.innerHTML = `[${instructionName}: ${element.textContent}]`;
          }
        }
      }
    });
    this.textData += `${htmlElement.textContent}\n`;
    const block = {
      type: 'paragraph',
      data: {
        text: html.innerHTML,
      },
    };

    this.copyBlocks.push(block as Block);
  }

  protected get instructionName() {
    return {
      [InstructionTypes.videoInstruction]: 'Video',
      [InstructionTypes.audioInstruction]: 'Audio',
      [InstructionTypes.imageInstruction]: 'Image',
      [InstructionTypes.textInstruction]: 'Text',
      [InstructionTypes.titleInstruction]: 'Title',
      [InstructionTypes.noteInstruction]: 'Note',
      [InstructionTypes.nameScriptInstruction]: 'Name in script',
      [InstructionTypes.documentInstruction]: 'Document page',
      [InstructionTypes.webPageInstruction]: 'Web page',
    };
  }
}

export default CopyHandler;
